import React, { useState } from "react";
import {
  Card,
  Switch,
  NumberInput,
  Text,
  Group,
  Stack,
  Title,
  Flex,
  Button,
  Skeleton,
  Alert,
} from "@mantine/core";
import css from "./Settings.module.scss";
import css2 from "./../../../Insights/pages/MeetingTime/AsAttendee/AsAttendee.module.scss";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useMutation, useQuery } from "react-query";
import { useUser } from "stores/useUser";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { FirestoreDB } from "utils/firebase";
import { notify } from "utils/common";
import {
  buildOrganizationSettingsUpdate,
  DEFAULT_EXTERNAL_COST_PER_HOUR,
  getOrganizationSettingsState,
} from "./settingsUtils";

const SettingsPage = () => {
  const [costAnonymityEnabled, setCostAnonymityEnabled] = useState(false);
  const [minAttendees, setMinAttendees] = useState(3);
  const [externalCostPerHour, setExternalCostPerHour] = useState(
    DEFAULT_EXTERNAL_COST_PER_HOUR
  );
  const [allowVideoRecording, setAllowVideoRecording] = useState(true);
  const [parent] = useAutoAnimate();
  const { user } = useUser();

  // Fetch organization settings
  const { isLoading: isLoadingSettings, error: settingsError } = useQuery(
    ["orgSettings", user?.subscription?.organization_id],
    async () => {
      if (!user?.subscription?.organization_id) return null;
      const orgRef = doc(
        FirestoreDB,
        "organizations",
        user.subscription.organization_id
      );
      const orgDoc = await getDoc(orgRef);
      return orgDoc.exists() ? orgDoc.data() : null;
    },
    {
      enabled: !!user?.subscription?.organization_id,
      onSuccess: (data) => {
        const settings = getOrganizationSettingsState(data);
        setCostAnonymityEnabled(settings.costAnonymityEnabled);
        setMinAttendees(settings.minAttendees);
        setAllowVideoRecording(settings.allowVideoRecording);
        setExternalCostPerHour(settings.externalCostPerHour);
      },
    }
  );
  const { mutate, isLoading } = useMutation({
    mutationFn: (data) => {
      const orgRef = doc(
        FirestoreDB,
        "organizations",
        user?.subscription.organization_id
      );
      return updateDoc(orgRef, data);
    },
    onError: () =>
      notify("Error updating settings. Please try again.", "error"),
    onSuccess: () => notify("Settings updated", "success"),
  });

  const handleSave = () => {
    mutate(
      buildOrganizationSettingsUpdate({
        costAnonymityEnabled,
        minAttendees,
        allowVideoRecording,
        externalCostPerHour,
      })
    );
  };

  // Show error state
  if (settingsError) {
    return (
      <div className={css2.container}>
        <Stack w={"100%"} py={30} align="center">
          <Flex justify="space-between" align="center" w={"100%"}>
            <Title order={3}>Settings</Title>
          </Flex>

          <Stack w={"100%"} sx={{ maxWidth: "50rem" }}>
            <Alert color="red" title="Error loading settings">
              Failed to load organization settings. Please refresh the page or try again later.
            </Alert>
          </Stack>
        </Stack>
      </div>
    );
  }

  return (
    <div className={css2.container}>
      <Stack w={"100%"} py={30} align="center">
        <Flex justify="space-between" align="center" w={"100%"}>
          <Title order={3}>Settings</Title>
        </Flex>

        <Stack w={"100%"} sx={{ maxWidth: "50rem" }}>
          {isLoadingSettings ? (
            // Loading skeleton
            <Card
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              className={css.settingsCard}
            >
              <Stack spacing="md">
                <Group position="apart" align="flex-start">
                  <div style={{ flex: 1 }}>
                    <Skeleton height={20} width="40%" mb={8} />
                    <Skeleton height={14} width="80%" />
                  </div>
                  <Skeleton height={28} width={50} radius="xl" />
                </Group>
              </Stack>
            </Card>
          ) : (
            // Actual content
            <Card
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              className={css.settingsCard}
            >
              <Stack spacing="md" ref={parent}>
                <Group position="apart" align="flex-start">
                  <div>
                    <Text weight={500} size="md" className={css.settingTitle}>
                      Cost Anonymity
                    </Text>
                    <Text
                      size="sm"
                      color="dimmed"
                      className={css.settingDescription}
                    >
                      Hide meeting costs when there are fewer attendees than the
                      specified threshold
                    </Text>
                  </div>
                  <Switch
                    checked={costAnonymityEnabled}
                    onChange={(event) =>
                      setCostAnonymityEnabled(event.currentTarget.checked)
                    }
                    size="md"
                    className={css.switch}
                  />
                </Group>

                {costAnonymityEnabled && (
                  <div className={css.conditionalInput}>
                    <NumberInput
                      label="Minimum attendees to show cost"
                      description="Meeting costs will only be visible when there are at least this many attendees"
                      value={minAttendees}
                      onChange={setMinAttendees}
                      min={2}
                      max={50}
                      step={1}
                      size="sm"
                      className={css.numberInput}
                    />
                  </div>
                )}

                <Group position="apart" align="flex-start">
                  <div>
                    <Text weight={500} size="md" className={css.settingTitle}>
                      External Attendee Cost
                    </Text>
                    <Text
                      size="sm"
                      color="dimmed"
                      className={css.settingDescription}
                    >
                      Hourly cost used for attendees outside your organization
                      when calculating meeting cost
                    </Text>
                  </div>
                  <NumberInput
                    value={externalCostPerHour}
                    onChange={setExternalCostPerHour}
                    min={1}
                    max={100000}
                    step={10}
                    size="sm"
                    icon="$"
                    parser={(value) => value.replace(/[^\d.]/g, "")}
                    formatter={(value) =>
                      value != null && value !== ""
                        ? `${value}/hr`
                        : ""
                    }
                    className={css.numberInput}
                  />
                </Group>

                <Group position="apart" align="flex-start">
                  <div>
                    <Text weight={500} size="md" className={css.settingTitle}>
                      Meeting Video Recording
                    </Text>
                    <Text
                      size="sm"
                      color="dimmed"
                      className={css.settingDescription}
                    >
                      Allow Gaintime Notetaker to save meeting video. When
                      disabled, transcripts, summaries, live notes, and
                      analytics are still created.
                    </Text>
                  </div>
                  <Switch
                    checked={allowVideoRecording}
                    onChange={(event) =>
                      setAllowVideoRecording(event.currentTarget.checked)
                    }
                    size="md"
                    className={css.switch}
                  />
                </Group>
              </Stack>
            </Card>
          )}

          {/* save button - only show when not loading */}
          {!isLoadingSettings && (
            <Button
              color="primary"
              radius="md"
              className={css.saveButton}
              onClick={handleSave}
              disabled={isLoading}
              loading={isLoading}
            >
              Save
            </Button>
          )}
        </Stack>
      </Stack>
    </div>
  );
};

export default SettingsPage;
